<template>
  <error-boundary module="view-manager-embedded-module">
    <div>
      <div class="employee-details-container col-centered">
        <div class="employee-details__header">
          <p class="employee-details__updated-at">
            {{ state.updatedAt || t('view-manager-module:loading') }}
          </p>
          <co-icon
            class="employee-details__print"
            name="print-solid"
            @click="print"
          ></co-icon>
        </div>
        <div
          v-for="(document, index) in state.documents"
          :key="index"
          class="employee-details__card DSCard-container"
        >
          <slot name="header">
            <div class="employee-details__header DSCard-header">
              <div class="employee-details__card__title">
                <span>{{ document.title }}</span>

                <co-button
                  v-if="
                    document.name === 'tfnDeclaration' && state.showExportToAto
                  "
                  type="text"
                  class="employee-details__card__action-button"
                  @click="goToTfnDeclaration"
                >
                  {{ t('view-manager-module:export-ato') }}
                </co-button>
              </div>
            </div>
          </slot>

          <hr-field-view
            v-for="(field, index) in document.fields"
            :key="`field-${index}`"
            :value="field"
            :is-sensitive-hidden="state.isSensitiveHidden"
          />
        </div>
      </div>
    </div>
  </error-boundary>
</template>

<script>
import HrFieldView from '@/components/hr-field-view.vue';
import store from '@/store';
import router from '@/router';
import setGlobalApiEndpoint from '@/lib/set-global-api-endpoint';
import jwtToObject from '@/lib/jwt-to-object';
import { DateTime } from 'luxon';
import { reactive, nextTick } from 'vue';
import { CoButton, CoIcon } from '@deputy/copilot2';
import cloneObj from '@/core/cloneObj';
import t from '@/lib/i18next';
import {
  filterSuperFieldsToBeIncluded,
  filterSuperOptionalFieldsToBeIncluded,
} from '@/store/composables/au/superannuation-rule';
import {
  filterTFNFieldsToBeIncluded,
  filterTFNFieldsToBeIncludedWhenNoOptionExists,
} from '@/store/composables/au/tfn-declaration-rule';
import { DOCUMENT_NAMES, TFN_DECLARATION } from '@/core/field-names';
import { logWebHrError } from '@/core/logger/log-wrapper';
import ErrorBoundary from '@/components/error-boundary.vue';

export default {
  name: 'view-manager-module',
  components: {
    ErrorBoundary,
    CoButton,
    CoIcon,
    HrFieldView,
  },
  props: {
    jwt: {
      type: String,
      default: null,
    },
    onboardingId: {
      type: String,
      default: null,
    },
  },
  setup(props) {
    if (!props.jwt) {
      return router.push({ name: 'BlockModule' });
    }

    const stateModule = store.state;

    const state = reactive({
      employeeName: '',
      updatedAt: '',
      documents: [],
      showExportToAto: false,
      isSensitiveHidden: false,
    });

    function goToTfnDeclaration() {
      router.push({
        name: 'before-print-editing',
      });
    }

    async function print() {
      state.isSensitiveHidden = true;
      await nextTick();
      window.print();
      state.isSensitiveHidden = false;
    }

    // TODO: find a better to filter out values like this
    function documentsToPreview(documents = []) {
      return documents.map((document) => {
        switch (document.name) {
          case 'superannuation':
            return filterWithNameBased(document);
          case 'tfnDeclaration':
            return documentsToPreviewTfnDeclaration(document);
          case 'taxDetails':
            return documentsToPreviewTaxDetails(document);
        }
        return document;
      });
    }

    const fieldFilterFunctions = {
      [DOCUMENT_NAMES.SUPERANNUATION]: filterSuperFieldsToBeIncluded,
      [DOCUMENT_NAMES.TFN_DECLARATION]: filterTFNFieldsToBeIncluded,
    };

    const optionalFieldFilterFunctions = {
      [DOCUMENT_NAMES.SUPERANNUATION]: filterSuperOptionalFieldsToBeIncluded,
      [DOCUMENT_NAMES.TFN_DECLARATION]:
        filterTFNFieldsToBeIncludedWhenNoOptionExists,
    };

    function filterWithNameBased(document) {
      const fieldNameValueMap = document.fields.reduce((prev, field) => {
        return {
          ...prev,
          [field.name]: {
            value: field.value,
            required: field?.validation?.required,
          },
        };
      }, {});

      const fieldsToBeIncluded =
        fieldFilterFunctions[document.name](fieldNameValueMap);

      const optionalFieldsToBeIncluded = optionalFieldFilterFunctions[
        document.name
      ]
        ? optionalFieldFilterFunctions[document.name](fieldNameValueMap)
        : [];

      const fields = document.fields.reduce((fields, current) => {
        if (
          !fieldsToBeIncluded.includes(current.name) &&
          !optionalFieldsToBeIncluded.includes(current.name)
        ) {
          current.hide = true;
        }
        return [...fields, current];
      }, []);

      document.fields = [...fields];

      return document;
    }

    function arePersonalDetailsFilled() {
      const employeeDetails = stateModule.documents.find(
        (item) => item.name === 'personalDetails'
      );
      const employeeDOB = employeeDetails.fields.find(
        (item) => item.name === 'personalDetails.dob'
      );
      const employeeAddress = employeeDetails.fields.find(
        (item) => item.name === 'personalDetails.address'
      );
      if (employeeDOB.value && employeeAddress.value) {
        return true;
      }
      return false;
    }

    function documentsToPreviewTfnDeclaration(document) {
      const TFN_OPTION_YES = 'Yes';
      const doc = filterWithNameBased(document);
      let hasValueCount = 0;
      const shownFields = doc.fields.filter((field) => {
        if (
          (field.value || field.name === TFN_DECLARATION.COUNTRY_OF_ORIGIN) &&
          !field.hide
        ) {
          hasValueCount += 1;
        }
        return !field.hide;
      });

      const tfnDeclarationOption = document.fields.find(
        (item) => item.name === TFN_DECLARATION.OPTION
      );

      const tfnDeclarationNumber = document.fields.find(
        (item) => item.name === TFN_DECLARATION.TFN
      );
      if (
        tfnDeclarationOption?.value === TFN_OPTION_YES ||
        (tfnDeclarationNumber?.value !== '' &&
          tfnDeclarationOption?.value === undefined)
      ) {
        if (
          hasValueCount === shownFields.length &&
          arePersonalDetailsFilled()
        ) {
          state.showExportToAto = true;
        }
      }

      return doc;
    }

    function documentsToPreviewTaxDetails(document = {}) {
      const hasStudentLoan = document.fields.some((item) => {
        return item.name === 'taxDetails.loan.option' && item.value === 'Yes';
      });

      const hasP45Form = document.fields.some((item) => {
        return item.name === 'taxDetails.p45' && item.value === 'Yes';
      });

      document.fields.forEach((item) => {
        if (item.name === 'taxDetails.loan.plans' && !hasStudentLoan) {
          item.hide = true;
        }
        if (item.name === 'taxDetails.p45upload' && !hasP45Form) {
          item.hide = true;
        }
      });

      return document;
    }

    function dateToHuman(updatedAt = new Date().toISOString()) {
      const updatedDate = DateTime.fromISO(updatedAt);
      const updated = updatedDate.toObject();
      updated.day = String(updated.day).padStart('2', '0');
      updated.month = String(updated.month).padStart('2', '0');

      return t(
        `view-manager-module:${
          ['APPROVED', 'SUBMITTED'].includes(stateModule.onboarding.state)
            ? 'submitted'
            : 'updated'
        }`,
        {
          ...updated,
          interpolation: { escapeValue: false },
        }
      );
    }

    async function initialize() {
      try {
        // TODO: implement temp jwt exchanging
        // const jwt = await $svcUrl.auth.token(props.jwt);

        setGlobalApiEndpoint(jwtToObject(props.jwt));

        await Promise.all([
          store.commit('setSession', {
            token: props.onboardingId,
            jwt: props.jwt,
          }),
          store.dispatch('getDocuments', props.onboardingId),
        ]);

        // TODO: remove the computed and add here the logic
        state.documents = documentsToPreview(cloneObj(stateModule.documents));
        state.employeeName = stateModule.Employee.employee.name;

        const { updatedAt } = stateModule.onboarding;
        state.updatedAt = dateToHuman(updatedAt);
      } catch (e) {
        logWebHrError({
          message: 'view manager embedded module error',
          source: 'view-manager-embedded-module',
          metadata: {
            onboardingId: props.onboardingId,
            documentLength: stateModule.documents?.length,
            hasEmployeeName: !!stateModule.Employee?.employee?.name,
            updatedAt: stateModule.onboarding?.updatedAt,
          },
          error: e,
        });
        return router.push({ name: 'BlockModule' });
      }
    }

    initialize();

    return {
      t,
      state,
      stateModule,
      print,
      goToTfnDeclaration,
    };
  },
};
</script>

<style scoped lang="scss">
@import '~@/core/style-vars.scss';
.employee-details-container {
  margin: 0 12px 0 0;
}
.employee-details__header {
  display: flex;
  justify-content: space-between;
}
.employee-details__DSCards {
  margin-bottom: 100px;
}
.employee-details__card {
  margin-bottom: 32px;
  padding-bottom: 32px;
  border-bottom: 1px solid var(--co-lines-border-transparent);
}
.employee-details__card__header {
  display: flex;
  border-bottom: 8px;
  border-color: var(--co-lines-border-solid);
}
.employee-details__card__title .DSIcon {
  color: var(--co-text-secondary);
}
.employee-details__card__title strong {
  color: var(--co-text-primary);
  margin-left: 5px;
}
.employee-details__card__title {
  flex-grow: 1;
  text-align: left;
  font-size: 18px;
  position: relative;
  padding: 0 6px;
  margin-bottom: 12px;
}
.employee-details__card__title span {
  font-weight: 600;
  color: var(--co-text-primary);
}
.employee-details__print {
  cursor: pointer;
  color: $co-button-default-text;
  width: 20px !important;
  height: 20px !important;
}
.employee-details__updated-at {
  padding: 16px 6px;
  color: var(--co-text-secondary);
}
.employee-details__title {
  color: var(--co-text-primary);
  margin-bottom: 100px;
}
.employee-details__card__action-button {
  float: right;
  font-size: 14px !important;
}
@media (max-width: 767px) {
  .employee-details .hr-module-header {
    margin-top: 0;
    font-size: 14px;
    text-align: left;
  }
  .employee-details__title h1 {
    margin-bottom: 0px;
    font-size: 18px;
  }
  .employee-details__print {
    right: 18px;
    font-size: 18px;
  }
}
</style>
